// frontend/src/modules/reports/api.ts
//
// Phase 4 (Reports Frontend), Task 1. Thin client over the frozen Phase-3
// backend contract (api/modules/reports/routes.ts + financial.routes.ts):
// `{ report: ... }` JSON by default, `?format=csv` (exact match) streams
// text/csv. `reportsApi.<report>(params)` returns the unwrapped report;
// `csvUrl(report, params)` builds the same query string plus `format=csv`
// for the CSV-download button (see ReportShell).
//
// CSV downloads reuse the payroll module's authenticated
// blob-download helper (a bare `<a href>` would 401 — auth here is
// header-only, no cookies).

import { api } from '../../core/api-client';
import { downloadFile } from '../payroll/api';
import type {
  HoursSummaryReport, GroupBy,
  ExceptionsReport, ExceptionType,
  OvertimeReport,
  LiveRosterReport,
  IdleBreaksReport,
  LeaveReport,
  TimelineReport,
  RevenueReport, RevenueGroupBy,
  AgingReport,
  ExpensesReport,
  PayrollCostReport, PayrollGroupBy,
  ProfitabilityReport,
  PnlLiteReport,
  CashTrendReport,
} from './types';

const BASE_WORKFORCE = '/api/reports/workforce';
const BASE_FINANCIAL = '/api/reports/financial';

export type ReportKey =
  | 'hours-summary' | 'exceptions' | 'overtime' | 'live-roster' | 'idle-breaks' | 'leave' | 'timeline'
  | 'revenue' | 'invoice-aging' | 'expenses' | 'payroll-cost' | 'project-profitability' | 'pnl-lite' | 'cash-trend';

// ─── per-report param shapes (frontend-facing, camelCase) ───────────────

export interface HoursSummaryParams { from?: string; to?: string; groupBy?: GroupBy; employeeId?: string; }
export interface ExceptionsParams { from?: string; to?: string; employeeId?: string; type?: ExceptionType; }
export interface OvertimeParams { from?: string; to?: string; groupBy?: 'day' | 'week'; employeeId?: string; }
export interface IdleBreaksParams { from?: string; to?: string; employeeId?: string; }
export interface LeaveParams { from?: string; to?: string; employeeId?: string; }
export interface TimelineParams { employeeId: string; date: string; }
export interface RevenueParams { from?: string; to?: string; groupBy?: RevenueGroupBy; clientId?: string; }
export interface AgingParams { clientId?: string; }
export interface ExpensesParams { from?: string; to?: string; categoryId?: string; status?: string; }
export interface PayrollCostParams { from?: string; to?: string; groupBy?: PayrollGroupBy; employeeId?: string; }
export interface ProfitabilityParams { from?: string; to?: string; projectId?: string; }
export interface PnlLiteParams { from?: string; to?: string; }
export interface CashTrendParams { from?: string; to?: string; groupBy?: RevenueGroupBy; }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyParams = any;

/** Backend base path per report (workforce vs financial routers). */
const PATHS: Record<ReportKey, string> = {
  'hours-summary': `${BASE_WORKFORCE}/hours-summary`,
  'exceptions': `${BASE_WORKFORCE}/exceptions`,
  'overtime': `${BASE_WORKFORCE}/overtime`,
  'live-roster': `${BASE_WORKFORCE}/live-roster`,
  'idle-breaks': `${BASE_WORKFORCE}/idle-breaks`,
  'leave': `${BASE_WORKFORCE}/leave`,
  'timeline': `${BASE_WORKFORCE}/timeline`,
  'revenue': `${BASE_FINANCIAL}/revenue`,
  'invoice-aging': `${BASE_FINANCIAL}/invoice-aging`,
  'expenses': `${BASE_FINANCIAL}/expenses`,
  'payroll-cost': `${BASE_FINANCIAL}/payroll-cost`,
  'project-profitability': `${BASE_FINANCIAL}/project-profitability`,
  'pnl-lite': `${BASE_FINANCIAL}/pnl-lite`,
  'cash-trend': `${BASE_FINANCIAL}/cash-trend`,
};

/** camelCase frontend params -> snake_case backend query keys, per report. */
const QUERY: Record<ReportKey, (p: AnyParams) => Record<string, string | undefined>> = {
  'hours-summary': (p: HoursSummaryParams) => ({ from: p.from, to: p.to, group_by: p.groupBy, employee_id: p.employeeId }),
  'exceptions': (p: ExceptionsParams) => ({ from: p.from, to: p.to, employee_id: p.employeeId, type: p.type }),
  'overtime': (p: OvertimeParams) => ({ from: p.from, to: p.to, group_by: p.groupBy, employee_id: p.employeeId }),
  'live-roster': () => ({}),
  'idle-breaks': (p: IdleBreaksParams) => ({ from: p.from, to: p.to, employee_id: p.employeeId }),
  'leave': (p: LeaveParams) => ({ from: p.from, to: p.to, employee_id: p.employeeId }),
  'timeline': (p: TimelineParams) => ({ employee_id: p.employeeId, date: p.date }),
  'revenue': (p: RevenueParams) => ({ from: p.from, to: p.to, group_by: p.groupBy, client_id: p.clientId }),
  'invoice-aging': (p: AgingParams) => ({ client_id: p.clientId }),
  'expenses': (p: ExpensesParams) => ({ from: p.from, to: p.to, category_id: p.categoryId, status: p.status }),
  'payroll-cost': (p: PayrollCostParams) => ({ from: p.from, to: p.to, group_by: p.groupBy, employee_id: p.employeeId }),
  'project-profitability': (p: ProfitabilityParams) => ({ from: p.from, to: p.to, project_id: p.projectId }),
  'pnl-lite': (p: PnlLiteParams) => ({ from: p.from, to: p.to }),
  'cash-trend': (p: CashTrendParams) => ({ from: p.from, to: p.to, group_by: p.groupBy }),
};

function qs(params: Record<string, string | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') usp.set(k, v);
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

function buildUrl(report: ReportKey, params: AnyParams): string {
  return `${PATHS[report]}${qs(QUERY[report](params ?? {}))}`;
}

/**
 * The plain JSON URL (no `format=csv`) for a report + params — useful when a
 * page wants `useApi`'s built-in loading/error/reload state instead of
 * calling `reportsApi.<report>()` directly (which already unwraps `.report`
 * but has no shared loading/error tracking).
 */
export function reportUrl(report: ReportKey, params: AnyParams = {}): string {
  return buildUrl(report, params);
}

/**
 * Same query string a report's JSON fetch uses, plus `format=csv` — the
 * ReportShell CSV button downloads this URL via `downloadReportCsv` below.
 * `format=csv` must be an EXACT-match string (see reports/shared.ts:respond).
 */
export function csvUrl(report: ReportKey, params: AnyParams = {}): string {
  return `${PATHS[report]}${qs({ ...QUERY[report](params ?? {}), format: 'csv' })}`;
}

/** Authenticated CSV download for a report + its current filter params. */
export function downloadReportCsv(report: ReportKey, params: AnyParams, filename: string): Promise<void> {
  return downloadFile(csvUrl(report, params), filename);
}

export const reportsApi = {
  hoursSummary: (p: HoursSummaryParams = {}) =>
    api<{ report: HoursSummaryReport }>(buildUrl('hours-summary', p)).then((r) => r.report),
  exceptions: (p: ExceptionsParams = {}) =>
    api<{ report: ExceptionsReport }>(buildUrl('exceptions', p)).then((r) => r.report),
  overtime: (p: OvertimeParams = {}) =>
    api<{ report: OvertimeReport }>(buildUrl('overtime', p)).then((r) => r.report),
  liveRoster: () =>
    api<{ report: LiveRosterReport }>(buildUrl('live-roster', {})).then((r) => r.report),
  idleBreaks: (p: IdleBreaksParams = {}) =>
    api<{ report: IdleBreaksReport }>(buildUrl('idle-breaks', p)).then((r) => r.report),
  leave: (p: LeaveParams = {}) =>
    api<{ report: LeaveReport }>(buildUrl('leave', p)).then((r) => r.report),
  timeline: (p: TimelineParams) =>
    api<{ report: TimelineReport }>(buildUrl('timeline', p)).then((r) => r.report),

  revenue: (p: RevenueParams = {}) =>
    api<{ report: RevenueReport }>(buildUrl('revenue', p)).then((r) => r.report),
  invoiceAging: (p: AgingParams = {}) =>
    api<{ report: AgingReport }>(buildUrl('invoice-aging', p)).then((r) => r.report),
  expenses: (p: ExpensesParams = {}) =>
    api<{ report: ExpensesReport }>(buildUrl('expenses', p)).then((r) => r.report),
  payrollCost: (p: PayrollCostParams = {}) =>
    api<{ report: PayrollCostReport }>(buildUrl('payroll-cost', p)).then((r) => r.report),
  projectProfitability: (p: ProfitabilityParams = {}) =>
    api<{ report: ProfitabilityReport }>(buildUrl('project-profitability', p)).then((r) => r.report),
  pnlLite: (p: PnlLiteParams = {}) =>
    api<{ report: PnlLiteReport }>(buildUrl('pnl-lite', p)).then((r) => r.report),
  cashTrend: (p: CashTrendParams = {}) =>
    api<{ report: CashTrendReport }>(buildUrl('cash-trend', p)).then((r) => r.report),
};
